import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import { debounce, omit } from 'lodash'
import I18n from 'core/i18n'
import { IJsonResponse } from 'core/JsonApiSerializer'
import { guideReportService, utilService } from 'core/services'
import { Button, InputTextArea, Select, Panel, InputText } from 'components/atoms'
import { Form } from 'components/molecules'
import { IWindow, IPaymentDetail, IAgentRank } from 'static/interfaces'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'
import { getSafePath } from 'utils/url'
import MultipleExpenseInput from 'components/molecules/GuideReport/ExpenseInput'
import MultipleImageInput from 'components/molecules/GuideReport/ImageInput'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: IWindow

interface IProps {
  guide_report: IJsonResponse
  isNew: boolean
  default_number: {
    num_of_adults: number
    num_of_children: number
    num_of_infants: number
  }
  postName: string
  guide: {
    id: number
    username: string
    fullname: string
  }
  user: any
  progresses: any
  rank: IAgentRank
  // 特殊ガイド(人数連動手当の対象外)
  isSpecial: boolean
}

const FIELDS = {
  num_of_adults: 'num_of_adults',
  num_of_children: 'num_of_children',
  num_of_infants: 'num_of_infants',
  number_change_reason: 'number_change_reason',
  report: 'report',
  problem: 'problem',
  progress: 'progress',
}

const PEOPLE_COUNTS = [
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  21,
  22,
  23,
  24,
  25,
]

interface INumOfPeople {
  num_of_adults: number
  num_of_children: number
  num_of_infants: number
}

const GuideReportEdit: React.FC<IProps> = props => {
  const { data: guideReport } = utilService.getDataFromJson(props.guide_report)
  const { data: user } = utilService.getDataFromJson(props.user)
  const [expenses, setExpenses] = React.useState(guideReport.expenses)
  const [images, setImages] = React.useState(guideReport.images)
  const [paymentDetail, setPaymentDetail] = React.useState<IPaymentDetail>(
    guideReport.payment_detail || {
      other_fee: 0,
      travel_cost: 0,
      deduction: 0,
      calculated_special_activity_fee: 0,
      calculated_large_group_fee: 0,
      guide_fee: 0,
    }
  )
  // 不正な入力値(数字列以外の文字列)の場合にtrueを格納
  const [paymentFieldErrors, setPaymentFieldErrors] = React.useState<
    Partial<Record<keyof IPaymentDetail, boolean>>
  >({})

  const handleChangePaymentField = (field: keyof IPaymentDetail) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = e.target.value
    setPaymentFieldErrors(prev => ({ ...prev, [field]: /[^0-9]/.test(raw) }))
    const numeric = raw.replace(/[^0-9]/g, '')
    setPaymentDetail(prev => ({ ...prev, [field]: numeric === '' ? 0 : Number(numeric) }))
  }
  const [saving, setSaving] = React.useState<boolean>(false)
  const params = new URLSearchParams(window.location.search)
  const tourGuideId = params.get('tour_guide_id')
  const returnToPath = getSafePath('return_to', '/agent/guide_reports')

  const { default_number } = props
  // 特殊ガイドは人数入力欄を出さないため、Formの対象フィールドからも除外する
  const formFields = props.isSpecial
    ? omit(FIELDS, ['num_of_adults', 'num_of_children', 'num_of_infants', 'number_change_reason'])
    : FIELDS
  const adultsRef = React.useRef()
  const childrenRef = React.useRef()
  const infantsRef = React.useRef()
  const [newNumber, setNewNumber] = React.useState<INumOfPeople>({
    num_of_adults: guideReport.id ? guideReport.num_of_adults : default_number.num_of_adults,
    num_of_children: guideReport.id ? guideReport.num_of_children : default_number.num_of_children,
    num_of_infants: guideReport.id ? guideReport.num_of_infants : default_number.num_of_infants,
  })
  const [progress, setProgress] = React.useState<string>(
    guideReport.id ? guideReport.progress : 'not_yet'
  )
  // 受領拒否は保存時に支払明細が全額0になるため、金額欄はすべて0表示にし、手入力欄は入力不可にする
  const isRejected = progress === 'rejected'
  const [guideFee, setGuideFee] = React.useState<number>(paymentDetail.guide_fee || 0)
  const [specialActivityFee, setSpecialActivityFee] = React.useState<number>(
    paymentDetail.calculated_special_activity_fee || 0
  )
  const [largeGroupFee, setLargeGroupFee] = React.useState<number>(
    paymentDetail.calculated_large_group_fee || 0
  )

  // 業務委託料・手当(特別アクティビティ費・大人数手当)はサーバーを唯一の計算元とし、
  // 人数やステータスの変更時にサーバーへ問い合わせて表示を更新する(連打を避けるため debounce)。
  // ステータスを渡すと、そのステータスで保存した後の値(受領拒否なら0、解除なら通常計算)を試算して返す
  const fetchCalculatedFees = React.useMemo(
    () =>
      debounce(async (numOfPeople: INumOfPeople, nextProgress: string) => {
        if (!guideReport.id) {
          return
        }
        const fees = await guideReportService.calcPayment(guideReport.id, numOfPeople, nextProgress)
        setGuideFee(fees.guide_fee)
        setSpecialActivityFee(fees.calculated_special_activity_fee)
        setLargeGroupFee(fees.calculated_large_group_fee)
      }, 400),
    [guideReport.id]
  )

  const handleChangeProgress = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setProgress(e.target.value)
      fetchCalculatedFees(newNumber, e.target.value)
    },
    [newNumber, fetchCalculatedFees]
  )

  const handleChangeNumber = React.useCallback(
    (ref, isInitial = false) => {
      const newVal = ref.current.value
      const name = ref.current.getAttribute('name')
      ref.current.style.color = Number(newVal) !== default_number[name] ? 'red' : 'black'
      const newValues = { ...newNumber, [name]: Number(newVal) }
      setNewNumber(newValues)
      if (!isInitial) {
        fetchCalculatedFees(newValues, progress)
      }
    },
    [default_number, newNumber, progress, fetchCalculatedFees]
  )

  const handleSubmit = React.useCallback(
    async (initialValues, submitValues) => {
      if (Object.values(paymentFieldErrors).some(Boolean)) {
        window.flashMessages.addMessage({ text: '支払金額に不正な入力があります', type: 'error' })
        return
      }
      // 受領拒否にすると保存時に支払明細が除外(全金額0)されるため、変更時のみ確認する
      if (
        submitValues.progress === 'rejected' &&
        guideReport.progress !== 'rejected' &&
        !window.confirm('受領拒否に更新すると、支払明細は全て0円に上書きされます。よろしいですか？')
      ) {
        return
      }
      setSaving(true)
      const formData = new FormData()

      Object.keys(submitValues).forEach(key => {
        formData.append(`guide_report_attributes[${key}]`, submitValues[key])
      })

      if (guideReport.id) {
        formData.append(`guide_report_attributes[id]`, guideReport.id)
        formData.append(`guide_report_attributes[lock_version]`, guideReport.lock_version)
      } else {
        formData.append(`guide_report_attributes[tour_guide_id]`, tourGuideId)
      }

      formData.append(
        `payment_detail_attributes[other_fee]`,
        isRejected ? '0' : paymentDetail.other_fee?.toString() || '0'
      )
      formData.append(
        `payment_detail_attributes[travel_cost]`,
        isRejected ? '0' : paymentDetail.travel_cost?.toString() || '0'
      )
      formData.append(
        `payment_detail_attributes[deduction]`,
        isRejected ? '0' : paymentDetail.deduction?.toString() || '0'
      )
      // 人数連動手当(calculated_special_activity_fee / calculated_large_group_fee)はサーバーが読み出し時に計算するため送信しない

      // 0件のときは削除するために空要素を送る
      if (expenses.length === 0) {
        formData.append(`expenses_attributes[]`, '')
      }
      expenses.forEach((expense, index) => {
        Object.keys(expense).forEach(key => {
          if (!['image_url', 'image', 'id', 'content_type', 'filename'].includes(key)) {
            formData.append(`expenses_attributes[][${key}]`, expense[key])
          }
        })
        if (!!expense.image) {
          formData.append(`expenses_attributes[][image]`, expense.image)
        }
        formData.append(`expenses_attributes[][position]`, index)
        if (!!expense.id) {
          formData.append(`expenses_attributes[][id]`, expense.id)
        }
      })
      // 0件のときは削除するために空要素を送る
      if (images.length === 0) {
        formData.append(`images_attributes[]`, '')
      }
      images.forEach((image, index) => {
        formData.append(`images_attributes[][description]`, image.description)
        formData.append(`images_attributes[][position]`, index)
        if (!!image.image) {
          formData.append(`images_attributes[][image]`, image.image)
        }
        if (!!image.id) {
          formData.append(`images_attributes[][id]`, image.id)
        }
      })

      try {
        if (props.isNew) {
          const { flush } = await guideReportService.createGuideReport(formData)
          window.flashMessages.addMessage({ text: flush.message, type: flush.type })
          location.href = returnToPath
        } else {
          const { flush } = await guideReportService.updateGuideReport(guideReport, formData)
          window.flashMessages.addMessage({ text: flush.message, type: flush.type })
          location.href = returnToPath
        }
      } catch (e) {
        console.error(e)
      }
      setSaving(false)
    },
    [
      guideReport,
      expenses,
      tourGuideId,
      images,
      paymentDetail,
      paymentFieldErrors,
      returnToPath,
      isRejected,
    ]
  )

  React.useEffect(() => {
    if (props.isSpecial) {
      return
    }
    handleChangeNumber(adultsRef, true)
    handleChangeNumber(childrenRef, true)
    handleChangeNumber(infantsRef, true)
  }, [])

  return (
    <SidebarLayout user={user}>
      <S.guideReportEdit>
        <Panel title={I18n.t('generic.guide_report')}>
          <div className="info">
            <div>
              <span className="infoTitle">Tour group ID:</span> {guideReport.tour_group_id}
            </div>
            <div>
              <span className="infoTitle">Tour name:</span> {props.postName}
            </div>
            <div>
              <a href={`/agent/guides/${props.guide.id}/edit`}>
                <span className="infoTitle">Guide name:</span> {props.guide.fullname}
              </a>
            </div>
            <div>
              <span className="infoTitle">Guide rank:</span> {props.rank?.name || '-'}
            </div>
          </div>
          <Form fields={formFields} handleSubmit={handleSubmit}>
            <div className="item">
              <div className="itemTitle">
                {I18n.t('guide_report.progress')} <span className="required">*</span>
              </div>
              <Select
                required={true}
                name="progress"
                options={Object.keys(props.progresses).map(key => ({
                  label: props.progresses[key],
                  value: key,
                }))}
                defaultValue={guideReport.id ? guideReport.progress : 'not_yet'}
                onChangeHandler={handleChangeProgress}
              />
            </div>
            {/* 特殊ガイドは予約人数がないため人数の変更はしない */}
            {!props.isSpecial && (
              <>
                <div className="item">
                  <div className="itemTitle">
                    {I18n.t('guide_report.num_of_adults')} <span className="required">*</span>
                  </div>
                  <Select
                    required={true}
                    name="num_of_adults"
                    options={PEOPLE_COUNTS.map(count => ({ label: count, value: count }))}
                    defaultValue={
                      guideReport.id ? guideReport.num_of_adults : default_number.num_of_adults
                    }
                    selectRef={adultsRef}
                    onChangeHandler={e => handleChangeNumber(adultsRef)}
                  />
                  <div className="infoText">Reserved: {default_number.num_of_adults}</div>
                </div>
                <div className="item">
                  <div className="itemTitle">
                    {I18n.t('guide_report.num_of_children')} <span className="required">*</span>
                  </div>
                  <Select
                    required={true}
                    name="num_of_children"
                    options={PEOPLE_COUNTS.map(count => ({ label: count, value: count }))}
                    defaultValue={
                      guideReport.id ? guideReport.num_of_children : default_number.num_of_children
                    }
                    selectRef={childrenRef}
                    onChangeHandler={e => handleChangeNumber(childrenRef)}
                  />
                  <div className="infoText">Reserved: {default_number.num_of_children}</div>
                </div>
                <div className="item">
                  <div className="itemTitle">
                    {I18n.t('guide_report.num_of_infants')} <span className="required">*</span>
                  </div>
                  <Select
                    required={true}
                    name="num_of_infants"
                    options={PEOPLE_COUNTS.map(count => ({ label: count, value: count }))}
                    defaultValue={
                      guideReport.id ? guideReport.num_of_infants : default_number.num_of_infants
                    }
                    selectRef={infantsRef}
                    onChangeHandler={e => handleChangeNumber(infantsRef)}
                  />
                  <div className="infoText">Reserved: {default_number.num_of_infants}</div>
                </div>
                <div className="item">
                  <div className="itemTitle">{I18n.t('guide_report.number_change_reason')}</div>
                  <InputTextArea
                    required={false}
                    name="number_change_reason"
                    placeholder={I18n.t('guide_report.number_change_reason')}
                    defaultValue={guideReport.number_change_reason}
                  />
                </div>
              </>
            )}
            <div className="item">
              <div className="itemTitle">{I18n.t('guide_report.report')}</div>
              <InputTextArea
                required={false}
                name="report"
                placeholder={I18n.t('guide_report.report')}
                defaultValue={guideReport.report}
              />
            </div>
            <div className="item">
              <div className="itemTitle">{I18n.t('guide_report.problem')}</div>
              <InputTextArea
                required={false}
                name="problem"
                placeholder={I18n.t('guide_report.problem')}
                defaultValue={guideReport.problem}
              />
            </div>
            <MultipleImageInput images={images} setImages={setImages} />
            <MultipleExpenseInput expenses={expenses} setExpenses={setExpenses} />
            <div className="item">
              <div className="itemTitle">
                {I18n.t('activerecord.attributes.payment_detail.guide_fee')}
              </div>
              <InputText
                required={false}
                readonly={true}
                type="number"
                name="guide_fee"
                placeholder={I18n.t('activerecord.attributes.payment_detail.guide_fee')}
                value={guideFee}
              />
            </div>
            {!props.isSpecial && (
              <>
                <div className="item">
                  <div className="itemTitle">
                    {I18n.t('activerecord.attributes.payment_detail.special_activity_fee')}
                  </div>
                  <InputText
                    required={false}
                    readonly={true}
                    type="number"
                    name="calculated_special_activity_fee"
                    placeholder={I18n.t(
                      'activerecord.attributes.payment_detail.special_activity_fee'
                    )}
                    value={specialActivityFee}
                  />
                  <div className="infoText">
                    {default_number.num_of_adults}/{default_number.num_of_children}/
                    {default_number.num_of_infants} →{' '}
                    <span
                      className={
                        default_number.num_of_adults === newNumber.num_of_adults
                          ? 'text-default'
                          : 'text-red'
                      }
                    >
                      {newNumber.num_of_adults}
                    </span>
                    /
                    <span
                      className={
                        default_number.num_of_children === newNumber.num_of_children
                          ? 'text-default'
                          : 'text-red'
                      }
                    >
                      {newNumber.num_of_children}
                    </span>
                    /
                    <span
                      className={
                        default_number.num_of_infants === newNumber.num_of_infants
                          ? 'text-default'
                          : 'text-red'
                      }
                    >
                      {newNumber.num_of_infants}
                    </span>
                  </div>
                </div>
                <div className="item">
                  <div className="itemTitle">
                    {I18n.t('activerecord.attributes.payment_detail.large_group_fee')}
                  </div>
                  <InputText
                    required={false}
                    readonly={true}
                    type="number"
                    name="calculated_large_group_fee"
                    placeholder={I18n.t('activerecord.attributes.payment_detail.large_group_fee')}
                    value={largeGroupFee}
                  />
                </div>
              </>
            )}
            <div className="item">
              <div className="itemTitle">
                {I18n.t('activerecord.attributes.payment_detail.other_fee')}
              </div>
              <InputText
                required={false}
                type="text"
                inputMode="numeric"
                name="other_fee"
                placeholder={I18n.t('activerecord.attributes.payment_detail.other_fee')}
                value={isRejected ? 0 : paymentDetail.other_fee}
                disabled={isRejected}
                error={paymentFieldErrors.other_fee ? '半角数字のみ入力できます' : null}
                onChangeHandler={handleChangePaymentField('other_fee')}
              />
            </div>
            <div className="item">
              <div className="itemTitle">
                {I18n.t('activerecord.attributes.payment_detail.travel_cost')}
              </div>
              <InputText
                required={false}
                type="text"
                inputMode="numeric"
                name="travel_cost"
                placeholder={I18n.t('activerecord.attributes.payment_detail.travel_cost')}
                value={isRejected ? 0 : paymentDetail.travel_cost}
                disabled={isRejected}
                error={paymentFieldErrors.travel_cost ? '半角数字のみ入力できます' : null}
                onChangeHandler={handleChangePaymentField('travel_cost')}
              />
            </div>
            <div className="item">
              <div className="itemTitle">
                {I18n.t('activerecord.attributes.payment_detail.advance_cost')}
              </div>
              {/* 立替経費は入力中の経費明細の合計(保存はせずサーバー側で算出)。支払明細側の算出値は canceled / excluded で 0 になる */}
              <InputText
                required={false}
                readonly={true}
                type="number"
                name="calculated_advance_cost"
                placeholder={I18n.t('activerecord.attributes.payment_detail.advance_cost')}
                value={
                  isRejected ? 0 : expenses.reduce((sum, e) => sum + (Number(e.expense) || 0), 0)
                }
              />
            </div>
            <div className="item">
              <div className="itemTitle">
                {I18n.t('activerecord.attributes.payment_detail.deduction')}
              </div>
              <span className="minusSign">−</span>
              <InputText
                required={false}
                type="text"
                inputMode="numeric"
                name="deduction"
                placeholder={I18n.t('activerecord.attributes.payment_detail.deduction')}
                value={isRejected ? 0 : paymentDetail.deduction}
                disabled={isRejected}
                error={paymentFieldErrors.deduction ? '半角数字のみ入力できます' : null}
                onChangeHandler={handleChangePaymentField('deduction')}
              />
            </div>
            <S.Buttons>
              <div>
                <Button>
                  <a href={returnToPath}>
                    <SvgIcon
                      name="keyboard_arrow_left"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    {I18n.t('generic.back_to_index')}
                  </a>
                </Button>
              </div>
              <div>
                <Button primary={true} disabled={saving}>
                  {I18n.t(saving ? 'generic.saving' : 'generic.save')}
                </Button>
              </div>
            </S.Buttons>
          </Form>
        </Panel>
      </S.guideReportEdit>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.guideReportEdit = styled.div`
  padding: 12px 0;
  max-width: 880px;
  margin: 0 auto;

  .info {
    display: flex;
    gap: 10px;
    font-size: 14px;
    margin-bottom: 30px;
    background: #f5f5f5;
    padding: 10px;
    border-radius: 6px;

    .infoTitle {
      color: gray;
      font-size: 12px;
    }
  }

  .item {
    display: flex;
    align-items: center;
    margin: 12px 0;

    .itemTitle {
      font-size: 14px;
      font-weight: bold;
      width: 180px;

      .required {
        color: #ba3b4a;
      }
    }

    .infoText {
      margin-left: 20px;
      font-size: 14px;
    }

    .minusSign {
      /* 入力欄の左端を他項目と揃えるため幅を持たせず、グリフだけ左側に表示する */
      width: 0;
      overflow: visible;
      position: relative;
      left: -16px;
      font-size: 16px;
      font-weight: bold;
    }

    .InputTextArea {
      flex: 1;
    }
  }

  hr {
    margin: 24px 0;
    border: none;
    border-top: 1px dotted #ccc;
  }
`

S.Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px;
`

export default GuideReportEdit
